import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, Globe, Loader2, User, Bot, RefreshCw, CheckCircle } from 'lucide-react';

import './chat.css';
import socket from '../../socket';
import { startMiloSession, sendMiloMessage, addMiloAgentAction, clearMiloAgentActions, clearMiloConversation } from '../../store/Campaign';

const Chat = ({ campaignId, onCampaignDataUpdate, handleFirstClick }) => {
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const [input, setInput] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);


  const { user } = useSelector(state => state['Auth']);
  const { miloSessionId, miloCampaignId, miloMessages = [], miloSuggestions = [], miloLoading = false, miloAgentActions = [] } = useSelector(state => state['Campaign']) || {};

  // Socket listeners for real-time updates
  // Note: User registration happens in socket.js on connect
  useEffect(() => {
    const handleMiloAction = (action) => {
      console.log('[Milo] Received milo:action:', action);
      dispatch(addMiloAgentAction(action));
    };

    // Handle real-time campaign preview updates from tool results
    const handleCampaignUpdate = (update) => {
      console.log('[Milo] Received milo:campaign-update via socket:', update);
      if (onCampaignDataUpdate) {
        onCampaignDataUpdate(update['toolName'], update['campaignData'], true);
      }
    };

    socket.on('milo:action', handleMiloAction);
    socket.on('milo:campaign-update', handleCampaignUpdate);

    return () => {
      console.log('[Milo] Cleaning up socket listeners');
      socket.off('milo:action', handleMiloAction);
      socket.off('milo:campaign-update', handleCampaignUpdate);
    };
  }, [user, dispatch, onCampaignDataUpdate]);

  // Start session on mount (only if no existing session for this campaign)
  useEffect(() => {
    // Start new session if no session exists or if session is for a different campaign
    if (!miloSessionId || (miloCampaignId && miloCampaignId !== campaignId)) {
      dispatch(startMiloSession({ campaignId }));
    }
  }, [dispatch, campaignId, miloSessionId, miloCampaignId]);

  // Show URL input only when conversation is at the beginning
  useEffect(() => {
    setShowUrlInput(miloMessages.length <= 1);
  }, [miloMessages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [miloMessages, miloAgentActions]);

  // Auto-focus input after response
  useEffect(() => {
    if (!miloLoading) inputRef.current?.focus();
  }, [miloLoading]);

  const handleSendMessage = (content) => {
    if (!content?.trim() || miloLoading) return;
    setShowUrlInput(false);
    setInput('');
    dispatch(sendMiloMessage({ sessionId: miloSessionId, message: content, campaignId, onCampaignDataUpdate }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || miloLoading) return;
    handleSendMessage(input);
  };

  const handleWebsiteSubmit = () => {
    if (!websiteUrl.trim()) return;
    handleSendMessage(`Here's my website: ${websiteUrl}`);
    setWebsiteUrl('');
    setShowUrlInput(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim()) handleFormSubmit(e);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleRestart = () => {
    // Clear conversation from backend (database) and frontend
    dispatch(clearMiloConversation({
      sessionId: miloSessionId,
      campaignId,
      successCallback: () => dispatch(startMiloSession({ campaignId }))
    }));
  };

  const formatToolName = (toolName) => {
    const names = {
      scrape_website: 'Analyzing Website',
      generate_pitch: 'Generating Pitch',
      generate_pain_points: 'Creating Pain Points',
      recommend_channels: 'Recommending Channels',
      build_sequence: 'Building Sequence',
      generate_email_template: 'Writing Email',
      update_campaign: 'Updating Campaign',
      validate_channels: 'Validating Channels'
    };
    return names[toolName] || toolName?.replace(/_/g, ' ') || 'Processing';
  };
  const showWelcome = !miloLoading && miloMessages.length <= 1;
  const WelcomeScreen = ({ onQuickSend }) => {
    return (
      <div className="milo-welcome">
        <div className="milo-welcome-card">
          <img
            className="milo-welcome-logo"
            alt="milo"
            src={require('../../static/img/milo-chat.png')}
          />

          {/* <h2 className="milo-welcome-title">Hi, I’m Milo</h2> */}
          <h4>Hi, I’m Milo, your  campaign builder assistant. how can I help you today?</h4>
          {/* <p className="milo-welcome-subtitle">
            Ask anything, I can search, summarize, and help you get work done.
          </p> */}

          {miloMessages.length <= 1 && miloSuggestions.length > 0 && !miloLoading && (
            <div className="milo-chat-suggestions">
              {miloSuggestions.map((suggestion, idx) => (
                <button key={idx} className="milo-chat-suggestion-btn" onClick={() => handleSuggestionClick(suggestion)}>
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* <div className="milo-welcome-hint">
            Tip: press Enter to send, Shift+Enter for new line.
          </div> */}
        </div>
      </div>
    );
  };

  const renderAgentActions = () => {
    if (!miloLoading) return null;

    // Get unique tool actions (most recent status for each tool)
    const toolActions = miloAgentActions.filter(a => a['toolName']);
    const uniqueToolActions = [];
    const seenTools = new Set();

    for (let i = toolActions.length - 1; i >= 0; i--) {
      const action = toolActions[i];
      if (!seenTools.has(action['toolName'])) {
        seenTools.add(action['toolName']);
        uniqueToolActions.unshift(action);
      }
    }

    // Get tool_call actions for detailed list
    const toolCallActions = miloAgentActions.filter(a => a['actionType'] === 'tool_call');

    return (
      <div className="milo-chat-message milo-chat-message-assistant milo-chat-agent-actions-wrapper">
        <div className="milo-chat-message-avatar">
          <img alt="milo-img" src={require('../../static/img/milo-chat-icon.png')} />
        </div>
        <div className="milo-chat-agent-actions">
          {/* Header with pulsing dot or typing animation */}
          <div className="milo-chat-agent-header">
            {toolCallActions.length > 0 ? (
              <>
                <div className="milo-chat-pulse-dot"></div>
                <span>Milo is working...</span>
              </>
            ) : (
              <div className="milo-chat-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>

          {/* Tool badges showing which tools are being used */}
          {uniqueToolActions.length > 0 && (
            <div className="milo-chat-tool-badges">
              {uniqueToolActions.map((action, idx) => (
                <div key={idx} className={`milo-chat-tool-badge ${action['status'] === 'completed' ? 'completed' : 'active'}`}>
                  <Sparkles size={12} />
                  <span>{action['toolDisplayName'] || formatToolName(action['toolName'])}</span>
                  {action['status'] === 'completed' && <CheckCircle size={12} />}
                </div>
              ))}
            </div>
          )}

          {/* Detailed action list with descriptions and spinners */}
          {toolCallActions.length > 0 && (
            <ul className="milo-chat-actions-list">
              {toolCallActions.map((action, idx) => (
                <li key={idx} className={`milo-chat-action-item ${action['status']}`}>
                  <Sparkles size={14} />
                  <span>{action['description'] || action['toolDisplayName'] || formatToolName(action['toolName'])}</span>
                  {action['status'] === 'in_progress' && <span className="milo-chat-action-spinner"></span>}
                  {action['status'] === 'completed' && <CheckCircle size={14} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderMessage = (message) => {
    const isUser = message['role'] === 'user';

    return (
      <div key={message['id']} className={`milo-chat-message ${isUser ? 'milo-chat-message-user' : 'milo-chat-message-assistant'}`}>
        <div className="milo-chat-message-avatar">
          {isUser ? <User size={18} /> : <img alt="milo-img" src={require('../../static/img/milo-chat-icon.png')} />}
        </div>
        <div className="milo-chat-message-content">
          {isUser ? (
            <p>{message['content']}</p>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message['content']}</ReactMarkdown>
          )}
          {message['toolsUsed'] && message['toolsUsed'].length > 0 && (
            <div className="milo-chat-tools-used">
              <span className="milo-chat-tools-label">Tools used:</span>
              {message['toolsUsed'].map((tool, idx) => (
                <span key={idx} className="milo-chat-tool-tag">
                  <Sparkles size={10} />
                  {tool['displayName'] || formatToolName(tool['name'])}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };


  return (
    <div className="milo-chat-container" >
      <div className="milo-chat-header">
        <div className="top-btns-area">
          <button className="milo-chat-restart-btn refresh-btn" onClick={handleRestart} title="Restart conversation">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5297 8.04422C13.4201 8.02728 13.3082 8.03209 13.2005 8.05836C13.0929 8.08463 12.9916 8.13183 12.9026 8.19722C12.8136 8.2626 12.7387 8.34484 12.6823 8.43913C12.6259 8.53342 12.5891 8.63787 12.5741 8.74637C12.4039 9.88812 11.8627 10.9444 11.0319 11.7563C10.004 12.7691 8.61103 13.3379 7.15874 13.3379C5.70645 13.3379 4.31349 12.7691 3.28557 11.7563C2.25985 10.7414 1.68379 9.36636 1.68379 7.93274C1.68379 6.49912 2.25985 5.12404 3.28557 4.10919C4.08617 3.31676 5.11889 2.7929 6.23764 2.61171C6.75151 2.51955 7.27648 2.50412 7.79499 2.56596C8.5281 2.65303 9.23537 2.88746 9.87315 3.25479L8.77593 3.44032C8.66552 3.45731 8.55964 3.49582 8.46445 3.55359C8.36926 3.61136 8.28665 3.68724 8.22144 3.77681C8.15623 3.86639 8.10972 3.96788 8.08461 4.07537C8.0595 4.18285 8.0563 4.2942 8.07518 4.40292C8.09407 4.51164 8.13467 4.61557 8.19462 4.70866C8.25457 4.80175 8.33268 4.88214 8.4244 4.94515C8.51611 5.00816 8.61961 5.05254 8.72886 5.0757C8.83811 5.09886 8.95093 5.10034 9.06077 5.08005L12.0019 4.58422C12.2219 4.5464 12.4178 4.42406 12.5467 4.24397C12.6756 4.06388 12.727 3.84072 12.6895 3.62334L12.1864 0.719903C12.1712 0.60923 12.1335 0.502718 12.0757 0.406719C12.0179 0.310719 11.9411 0.227198 11.8498 0.161134C11.7586 0.09507 11.6549 0.0478169 11.5447 0.0221917C11.4346 -0.00343356 11.3204 -0.00690607 11.2089 0.0119811C11.0974 0.0308683 10.9909 0.0717285 10.8957 0.132125C10.8005 0.192522 10.7186 0.271219 10.6549 0.363524C10.5912 0.455829 10.547 0.559851 10.5249 0.669389C10.5028 0.778928 10.5033 0.891739 10.5263 1.0011L10.6577 1.76564C9.83364 1.30865 8.9254 1.01873 7.98629 0.912911C7.30499 0.831807 6.61519 0.852561 5.94016 0.974474C4.48206 1.21377 3.1368 1.89874 2.09396 2.93284C-0.697987 5.68985 -0.697987 10.1748 2.09396 12.9326C2.7586 13.5896 3.54819 14.1105 4.41737 14.4653C5.28655 14.82 6.21819 15.0018 7.15874 15C8.09929 15.0018 9.03092 14.82 9.9001 14.4653C10.7693 14.1105 11.5589 13.5896 12.2235 12.9326C13.3109 11.8683 14.0189 10.4842 14.241 8.98846C14.2736 8.77025 14.217 8.54821 14.0838 8.37115C13.9506 8.19408 13.7516 8.07649 13.5306 8.04422H13.5297Z" fill="currentColor" />
            </svg>
          </button>
          <button className={`milo-chat-restart-btn`} onClick={handleFirstClick} title="Expand">
            <svg width="10" height="13" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g >
                <line x1="0.597945" y1="0.597945" x2="0.597945" y2="11.3609" stroke="currentColor" stroke-width="1.19589" stroke-linecap="round" />
                <path d="M2.76563 5.41211C2.38676 5.79098 2.38676 6.41899 2.76563 6.79785L7.80371 11.8359C8.18258 12.2147 8.81061 12.2148 9.18945 11.8359C9.38583 11.6396 9.47754 11.3912 9.47754 11.1426C9.47744 10.8941 9.38567 10.6465 9.18945 10.4502L4.9082 6.16797L4.84473 6.10449L9.18945 1.75977C9.5683 1.38092 9.56826 0.752894 9.18945 0.374023C8.81059 -0.00484126 8.18258 -0.00483816 7.80371 0.374023L2.76563 5.41211Z" fill="currentColor" stroke="white" stroke-width="0.179383" />
              </g>
            </svg>
          </button>
        </div>
        <div className="text-area">
          <h3>Craft a Winning Pitch</h3>
          <p>A strong selling point can 3x your reply rates.</p>
        </div>
      </div>

      {/* WELCOME SCREEN (shows only at start) */}
      {showWelcome
        ? <div className="milo-chat-messages milo-chat-welcome"><WelcomeScreen onQuickSend={handleSendMessage} /></div>
        : <div className="milo-chat-messages">
          {miloMessages.map(renderMessage)}
          {renderAgentActions()}
          <div ref={messagesEndRef} />
        </div>
      }



      {/* {miloMessages.length <= 1 && miloSuggestions.length > 0 && !miloLoading && (
        <div className="milo-chat-suggestions">
          {miloSuggestions.map((suggestion, idx) => (
            <button key={idx} className="milo-chat-suggestion-btn" onClick={() => handleSuggestionClick(suggestion)}>
              {suggestion}
            </button>
          ))}
        </div>
      )} */}

      {showUrlInput && miloMessages.length <= 1 && (
        <div className="milo-chat-quick-action">
          <div className="milo-chat-url-input">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 0C2.6833 0 0 2.6923 0 6C0 9.30005 2.6736 12 6 12C9.31558 12 12 9.30852 12 6C12 2.69443 9.31829 0 6 0ZM10.2369 9.53786C9.76726 9.2965 9.23503 9.10793 8.71781 8.97367C8.96018 8.1467 9.09792 7.21361 9.11738 6.24H11.5148C11.4624 7.4508 11.0145 8.60762 10.2369 9.53786ZM1.76309 9.53786C0.985536 8.6076 0.537624 7.4508 0.485208 6.24H2.88259C2.90208 7.21361 3.03982 8.1467 3.28217 8.97367C2.76446 9.10822 2.24117 9.29311 1.76309 9.53786ZM1.76309 2.46211C2.2404 2.70665 2.76523 2.89176 3.28217 3.02633C3.03982 3.8533 2.90208 4.78637 2.88259 5.76H0.485208C0.537624 4.54918 0.985536 3.39238 1.76309 2.46211ZM8.56949 2.56968C8.32646 1.89221 7.96481 1.18469 7.45879 0.677064C8.37466 0.928632 9.2183 1.41415 9.89957 2.09371C9.48314 2.29469 9.0055 2.46067 8.56949 2.56968ZM6.24 2.87722V0.503688C6.82608 0.618528 7.3805 1.15174 7.82179 2.03441C7.92274 2.2363 8.01492 2.45119 8.09815 2.67708C7.48642 2.79984 6.85886 2.86411 6.24 2.87722ZM5.76 2.87722C5.13588 2.86402 4.50662 2.79869 3.90185 2.67708C3.98506 2.45117 4.07726 2.23627 4.17818 2.03441C4.61952 1.15174 5.17392 0.618504 5.76 0.503688V2.87722ZM5.76 3.35731V5.76H3.36266C3.38213 4.82086 3.51564 3.92441 3.74993 3.13536C4.3716 3.26402 5.05183 3.34291 5.76 3.35731ZM5.76 6.24V8.64269C5.09114 8.6563 4.40234 8.72906 3.74995 8.86464C3.51564 8.07559 3.38213 7.17914 3.36266 6.24H5.76ZM5.76 9.12278V11.4963C5.17392 11.3815 4.61952 10.8482 4.17818 9.96559C4.07724 9.7637 3.98506 9.54881 3.90185 9.32292C4.48332 9.20554 5.11608 9.13642 5.76 9.12278ZM6.24 9.12278C6.88243 9.13637 7.51262 9.20496 8.09815 9.32292C8.01492 9.54881 7.92274 9.7637 7.82179 9.96559C7.38048 10.8482 6.82608 11.3815 6.24 11.4963V9.12278ZM6.24 8.64269V6.24H8.63736C8.61787 7.17914 8.48438 8.07559 8.25005 8.86464C7.63205 8.73629 6.94723 8.65709 6.24 8.64269ZM6.24 5.76V3.35731C6.95614 3.34274 7.63375 3.26273 8.25007 3.13536C8.48438 3.92438 8.61787 4.82086 8.63736 5.76H6.24ZM3.43051 2.56968C2.98051 2.45604 2.51705 2.2951 2.10041 2.09371C2.7817 1.41415 3.62534 0.928632 4.54123 0.677064C4.03445 1.18543 3.67294 1.89386 3.43051 2.56968ZM3.43051 9.43032C3.67284 10.106 4.0344 10.8145 4.54123 11.3229C3.62534 11.0714 2.7817 10.5858 2.10041 9.90629C2.51767 9.7045 2.98135 9.5443 3.43051 9.43032ZM8.56946 9.43032C8.99954 9.53782 9.48334 9.70486 9.89959 9.90629C9.2183 10.5858 8.37466 11.0714 7.45879 11.3229C7.96558 10.8145 8.32718 10.1059 8.56946 9.43032ZM9.11741 5.76C9.09792 4.78637 8.96018 3.8533 8.71783 3.02633C9.2437 2.88977 9.77033 2.70139 10.2369 2.46214C11.0145 3.39238 11.4624 4.5492 11.5148 5.76002H9.11741V5.76Z" fill="black" fill-opacity="0.5" />
            </svg>
            <input
              type="text"
              placeholder="Enter your website URL"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleWebsiteSubmit()}
            />
            <button onClick={handleWebsiteSubmit} disabled={!websiteUrl.trim()}>
              <i className="icon">
                <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7.94442 0.260505C7.92756 0.186509 7.88606 0.120433 7.82673 0.0731046C7.7674 0.0257758 7.69376 0 7.61787 0C7.54197 0 7.46833 0.0257758 7.409 0.0731046C7.34967 0.120433 7.30817 0.186509 7.29131 0.260505L7.10593 1.07539C7.06393 1.26007 6.97058 1.42912 6.83665 1.56305C6.70273 1.69697 6.53367 1.79032 6.34899 1.83232L5.53394 2.01771C5.45995 2.03457 5.39387 2.07607 5.34654 2.13539C5.29921 2.19472 5.27344 2.26837 5.27344 2.34426C5.27344 2.42015 5.29921 2.4938 5.34654 2.55313C5.39387 2.61246 5.45995 2.65395 5.53394 2.67082L6.34899 2.8562C6.53367 2.8982 6.70273 2.99155 6.83665 3.12547C6.97058 3.2594 7.06393 3.42845 7.10593 3.61314L7.29131 4.42802C7.30817 4.50201 7.34967 4.56809 7.409 4.61542C7.46833 4.66274 7.54197 4.68852 7.61787 4.68852C7.69376 4.68852 7.7674 4.66274 7.82673 4.61542C7.88606 4.56809 7.92756 4.50201 7.94442 4.42802L8.1298 3.61314C8.17181 3.42845 8.26515 3.2594 8.39908 3.12547C8.533 2.99155 8.70206 2.8982 8.88674 2.8562L9.70162 2.67082C9.77562 2.65395 9.84169 2.61246 9.88902 2.55313C9.93635 2.4938 9.96213 2.42015 9.96213 2.34426C9.96213 2.26837 9.93635 2.19472 9.88902 2.13539C9.84169 2.07607 9.77562 2.03457 9.70162 2.01771L8.88674 1.83232C8.70206 1.79032 8.533 1.69697 8.39908 1.56305C8.26515 1.42912 8.17181 1.26007 8.1298 1.07539L7.94442 0.260505ZM6.88739 2.34426C7.20172 2.18376 7.45736 1.92812 7.61787 1.61378C7.77836 1.92812 8.034 2.18377 8.34834 2.34426C8.034 2.50476 7.77836 2.7604 7.61787 3.07474C7.45736 2.7604 7.20172 2.50476 6.88739 2.34426Z" fill="#6B21B0" />
                  <path d="M0.326131 5.35853C0.122997 5.35853 -0.0355913 5.18051 0.00694457 4.9819C0.4107 3.09173 2.09036 1.67432 4.10111 1.67432C4.18993 1.67432 4.27512 1.7096 4.33794 1.77241C4.40075 1.83523 4.43603 1.92042 4.43603 2.00924C4.43603 2.09807 4.40075 2.18326 4.33794 2.24607C4.27512 2.30889 4.18993 2.34417 4.10111 2.34417C2.43099 2.34417 1.03266 3.50838 0.673786 5.06965C0.63594 5.23393 0.4946 5.35853 0.326131 5.35853ZM0.673786 6.65218C0.63594 6.4879 0.4946 6.36331 0.326131 6.36331C0.122997 6.36331 -0.0355913 6.54132 0.00694457 6.73994C0.4107 8.6301 2.09036 10.0475 4.10111 10.0475C6.41328 10.0475 8.28771 8.17309 8.28771 5.86092C8.28771 5.77209 8.25242 5.6869 8.18961 5.62409C8.1268 5.56128 8.04161 5.52599 7.95278 5.52599C7.86395 5.52599 7.77876 5.56128 7.71595 5.62409C7.65314 5.6869 7.61785 5.77209 7.61785 5.86092C7.61785 7.80317 6.04335 9.37766 4.10111 9.37766C2.43099 9.37766 1.03266 8.21345 0.673786 6.65218Z" fill="#6B21B0" />
                  <path d="M2.09128 5.52617C2.00245 5.52617 1.91726 5.56146 1.85445 5.62427C1.79163 5.68708 1.75635 5.77227 1.75635 5.8611C1.75635 5.94993 1.79163 6.03512 1.85445 6.09793C1.91726 6.16074 2.00245 6.19603 2.09128 6.19603H3.76592V7.87067C3.76592 7.9595 3.8012 8.04469 3.86401 8.1075C3.92683 8.17031 4.01202 8.2056 4.10084 8.2056C4.18967 8.2056 4.27486 8.17031 4.33767 8.1075C4.40049 8.04469 4.43577 7.9595 4.43577 7.87067V6.19603H6.11041C6.19924 6.19603 6.28443 6.16074 6.34724 6.09793C6.41005 6.03512 6.44534 5.94993 6.44534 5.8611C6.44534 5.77227 6.41005 5.68708 6.34724 5.62427C6.28443 5.56146 6.19924 5.52617 6.11041 5.52617H4.43577V3.85153C4.43577 3.7627 4.40049 3.67751 4.33767 3.6147C4.27486 3.55189 4.18967 3.5166 4.10084 3.5166C4.01202 3.5166 3.92683 3.55189 3.86401 3.6147C3.8012 3.67751 3.76592 3.7627 3.76592 3.85153V5.52617H2.09128Z" fill="#6B21B0" />
                </svg>
              </i>
              Generate
            </button>
          </div>
          <span className="milo-chat-or">Or describe what do you want</span>
        </div>
      )}

      <form className="milo-chat-input-form" onSubmit={handleFormSubmit}>
        <div className="milo-chat-input-wrapper">
          <input
            ref={inputRef}
            type="text"
            placeholder="Ask anything...."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={miloLoading}
          />
          <div className="milo-chat-input-actions">
            <button className="add-btn-file">
              <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.035 14V10.895H5.93V9.59H9.035V6.5H10.34V9.59H13.43V10.895H10.34V14H9.035Z" fill="#120348" fill-opacity="0.49" />
                <rect x="0.5" y="0.5" width="18" height="19" rx="3.5" stroke="black" stroke-opacity="0.12" />
              </svg>
            </button>
            <button type="submit" disabled={!input.trim() || miloLoading}>
              {miloLoading ? <Loader2 size={18} className="milo-chat-spinner" /> : <span>Send <i className="icon ml-1"><svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.54399 7.09673L4.55271 3.11417C4.49734 3.003 4.47845 2.87718 4.49875 2.75465C4.51904 2.63211 4.57748 2.51911 4.66575 2.43172C4.75401 2.34434 4.8676 2.28703 4.99033 2.26796C5.11306 2.24889 5.23868 2.26903 5.34929 2.32552L13.6297 6.57184C13.726 6.62154 13.8069 6.6968 13.8633 6.78939C13.9197 6.88197 13.9496 6.98831 13.9496 7.09673C13.9496 7.20516 13.9197 7.31149 13.8633 7.40407C13.8069 7.49666 13.726 7.57192 13.6297 7.62162L5.34929 11.8679C5.23868 11.9248 5.11294 11.9452 4.99005 11.9262C4.86715 11.9073 4.75339 11.85 4.66502 11.7625C4.57666 11.675 4.51821 11.5618 4.49804 11.4391C4.47787 11.3164 4.49701 11.1905 4.55271 11.0793L6.54399 7.09673ZM5.32982 3.10229L7.24847 6.94025C7.29799 7.03863 7.29799 7.15483 7.24847 7.25321L5.32982 11.0912L13.1187 7.09673L5.32982 3.10229Z" fill="white" /><path fill-rule="evenodd" clip-rule="evenodd" d="M6.9352 7.44651C6.88925 7.44651 6.84375 7.43746 6.80129 7.41987C6.75884 7.40229 6.72026 7.37651 6.68777 7.34402C6.65527 7.31152 6.6295 7.27295 6.61191 7.23049C6.59433 7.18804 6.58528 7.14253 6.58528 7.09658C6.58528 7.05063 6.59433 7.00513 6.61191 6.96267C6.6295 6.92022 6.65527 6.88164 6.68777 6.84915C6.72026 6.81665 6.75884 6.79088 6.80129 6.77329C6.84375 6.75571 6.88925 6.74666 6.9352 6.74666L13.4062 6.74665C13.4522 6.74665 13.4977 6.75571 13.5401 6.77329C13.5826 6.79088 13.6211 6.81665 13.6536 6.84915C13.6861 6.88164 13.7119 6.92022 13.7295 6.96267C13.7471 7.00513 13.7561 7.05063 13.7561 7.09658C13.7561 7.14253 13.7471 7.18804 13.7295 7.23049C13.7119 7.27295 13.6861 7.31152 13.6536 7.34402C13.6211 7.37651 13.5826 7.40229 13.5401 7.41987C13.4977 7.43746 13.4522 7.44651 13.4062 7.44651L6.9352 7.44651Z" fill="currentColor" /></svg></i></span>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Chat;
